/**
 * The Data tab's second round, without React: CSV parsing and column mapping for "Import CSV", JSON / NDJSON for
 * "Import JSON", the document tree's paths and scalar edits, the Review dialog's summary sentence and Redis targets.
 */

export const CSV_MAX_ROWS = 5000;
export const JSON_MAX_DOCS = 10000;
export const SKIP = '';

// ---- CSV

/** The delimiter of the first line: the one of `,` `;` tab `|` that appears most outside quotes. */
export function sniffDelimiter(text) {
  const line = String(text || '').split(/\r?\n/, 1)[0] || '';
  const counts = { ',': 0, ';': 0, '\t': 0, '|': 0 };
  let quoted = false;
  for (const ch of line) {
    if (ch === '"') { quoted = !quoted; } else if (!quoted && ch in counts) { counts[ch] += 1; }
  }
  const [best, n] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return n > 0 ? best : ',';
}

/**
 * RFC 4180 CSV: quoted fields with "" for a quote, CRLF or LF, a BOM ignored, a last empty line dropped.
 * `{ rows: [[cell]], delimiter }`. `maxRows` stops reading after that many rows (+1, to know there were more).
 */
export function parseCsv(input, { delimiter, maxRows = Infinity } = {}) {
  const text = String(input || '').replace(/^\uFEFF/, '');
  const sep = delimiter || sniffDelimiter(text);
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  let i = 0;
  const endCell = () => { row.push(cell); cell = ''; };
  const endRow = () => { endCell(); rows.push(row); row = []; };
  while (i < text.length && rows.length <= maxRows) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i += 2; continue; }
      if (ch === '"') { quoted = false; i += 1; continue; }
      cell += ch;
      i += 1;
      continue;
    }
    if (ch === '"' && cell === '') { quoted = true; i += 1; continue; }
    if (ch === sep) { endCell(); i += 1; continue; }
    if (ch === '\r' && text[i + 1] === '\n') { endRow(); i += 2; continue; }
    if (ch === '\n' || ch === '\r') { endRow(); i += 1; continue; }
    cell += ch;
    i += 1;
  }
  if (rows.length <= maxRows && (cell !== '' || row.length)) { endRow(); }
  return { rows: rows.filter((r) => !(r.length === 1 && r[0] === '')), delimiter: sep };
}

const norm = (name) => String(name || '').toLowerCase().replace(/[\s_\-.]+/g, '');

/**
 * Each CSV header's table column (same name, ignoring case, spaces, `_`, `-`), or SKIP. A column is used once; a
 * generated column never.
 */
export function autoMap(headers, columns) {
  const used = new Set();
  return headers.map((header) => {
    const exact = columns.find((c) => c.name === header && !used.has(c.name) && !c.generated);
    const loose = exact || columns.find((c) => norm(c.name) === norm(header) && !used.has(c.name) && !c.generated);
    if (!loose) { return SKIP; }
    used.add(loose.name);
    return loose.name;
  });
}

const INT = /^(int|integer|smallint|bigint|int2|int4|int8|serial|bigserial|smallserial)$/i;
const NUM = /^(real|float|float4|float8|double precision|double|numeric.*|decimal.*|money)$/i;
const BOOL = /^(bool|boolean)$/i;
const JSONT = /^jsonb?$/i;
const DATE = /^(date|timestamp.*|timestamptz)$/i;
const UUID = /^uuid$/i;

/** Why a CSV value does not fit the column ("not a number"), or null. Empty = no value (the default / NULL). */
export function typeIssue(value, column = {}) {
  const v = String(value ?? '').trim();
  if (v === '' || v === 'NULL') { return null; }
  const type = String(column.type || '').trim();
  if (INT.test(type)) { return /^[-+]?\d+$/.test(v) ? null : 'not a whole number'; }
  if (NUM.test(type)) { return /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(v) ? null : 'not a number'; }
  if (BOOL.test(type)) { return /^(true|false|t|f|1|0|yes|no)$/i.test(v) ? null : 'not true or false'; }
  if (JSONT.test(type)) { try { JSON.parse(v); return null; } catch { return 'not JSON'; } }
  if (DATE.test(type)) { return Number.isNaN(Date.parse(v)) && !/^\d{4}-\d\d-\d\d/.test(v) ? 'not a date' : null; }
  if (UUID.test(type)) { return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) ? null : 'not a UUID'; }
  return null;
}

/**
 * The table's columns an import should fill but does not: NOT NULL, no default, not generated, not mapped.
 */
export function missingRequired(mapping, columns) {
  const mapped = new Set(mapping.filter(Boolean));
  return columns.filter((c) => c.nullable === false && (c.default === null || c.default === undefined) && !c.generated && !mapped.has(c.name)).map((c) => c.name);
}

/**
 * The staged inserts of the CSV's data rows: `{ values: { column: text }, line }` (empty cells left out, so the column
 * gets its default or NULL). `line`: the row's line in the file (1 = the first line).
 */
export function csvInserts(dataRows, mapping, { firstLine = 2 } = {}) {
  return dataRows.map((cells, index) => {
    const values = {};
    mapping.forEach((column, i) => {
      if (!column) { return; }
      const value = cells[i];
      if (value !== undefined && value !== '') { values[column] = value; }
    });
    return { values, line: firstLine + index };
  });
}

/** For each mapped column, the first few rows whose value does not fit: `{ column: { count, first: [{ line, value, issue }] } }`. */
export function typeIssues(inserts, columns) {
  const out = {};
  const byName = Object.fromEntries(columns.map((c) => [c.name, c]));
  inserts.forEach((ins) => {
    Object.entries(ins.values).forEach(([name, value]) => {
      const issue = typeIssue(value, byName[name]);
      if (!issue) { return; }
      out[name] = out[name] || { count: 0, first: [] };
      out[name].count += 1;
      if (out[name].first.length < 3) { out[name].first.push({ line: ins.line, value, issue }); }
    });
  });
  return out;
}

// ---- JSON

/**
 * The documents of a JSON file: a JSON array of objects, one object, or NDJSON (one object per line).
 * `{ ok, docs, format: 'array' | 'object' | 'ndjson' }` or `{ ok: false, error }`.
 */
export function parseJsonDocs(input) {
  const text = String(input || '').replace(/^\uFEFF/, '').trim();
  if (!text) { return { ok: false, error: 'The file is empty' }; }
  try {
    const value = JSON.parse(text);
    if (Array.isArray(value)) {
      const bad = value.findIndex((d) => !d || typeof d !== 'object' || Array.isArray(d));
      if (bad >= 0) { return { ok: false, error: `Item ${bad + 1} is not an object` }; }
      return { ok: true, docs: value, format: 'array' };
    }
    if (value && typeof value === 'object') { return { ok: true, docs: [value], format: 'object' }; }
    return { ok: false, error: 'The file is not a JSON object or array' };
  } catch (whole) {
    const lines = text.split(/\r?\n/);
    const docs = [];
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i].trim();
      if (!line) { continue; }
      let value;
      try { value = JSON.parse(line); } catch (e) {
        return { ok: false, error: lines.length > 1 ? `Line ${i + 1} is not JSON: ${e.message}` : `Not JSON: ${whole.message}` };
      }
      if (!value || typeof value !== 'object' || Array.isArray(value)) { return { ok: false, error: `Line ${i + 1} is not an object` }; }
      docs.push(value);
    }
    return docs.length ? { ok: true, docs, format: 'ndjson' } : { ok: false, error: 'No documents in the file' };
  }
}

// ---- Document tree

/** Extended JSON wrappers shown as one value: `{ $oid }`, `{ $date }`, `{ $numberLong }`, `{ $numberDecimal }`… */
export function ejsonLeaf(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) { return null; }
  const keys = Object.keys(value);
  if (keys.length !== 1 || !keys[0].startsWith('$')) { return null; }
  const key = keys[0];
  const inner = value[key];
  const labels = { $oid: 'ObjectId', $date: 'Date', $numberLong: 'Long', $numberDecimal: 'Decimal', $numberInt: 'Int', $numberDouble: 'Double', $uuid: 'UUID', $binary: 'Binary', $timestamp: 'Timestamp', $regularExpression: 'Regex' };
  if (!labels[key]) { return null; }
  const text = typeof inner === 'string' ? inner : inner && typeof inner === 'object' && typeof inner.$numberLong === 'string' ? inner.$numberLong : JSON.stringify(inner);
  return { key, type: labels[key], text, editable: typeof inner === 'string' && key !== '$oid' && key !== '$binary' };
}

/** The type a tree row shows. */
export function typeOf(value) {
  if (value === null) { return 'null'; }
  if (Array.isArray(value)) { return 'array'; }
  const leaf = ejsonLeaf(value);
  if (leaf) { return leaf.type; }
  return typeof value === 'object' ? 'object' : typeof value;
}

/** A copy of `doc` with the value at `path` (keys and array indexes) replaced. */
export function setAtPath(doc, path, value) {
  if (!path.length) { return value; }
  const [head, ...rest] = path;
  const copy = Array.isArray(doc) ? [...doc] : { ...doc };
  copy[head] = setAtPath(doc[head], rest, value);
  return copy;
}

/**
 * What an in-place edit of a scalar means, keeping its type: a number stays a number (or an error), a boolean takes
 * true/false, null takes any JSON value, a string stays the text typed; an EJSON `{ $date: … }` keeps its wrapper.
 * `{ ok, value }` or `{ ok: false, error }`.
 */
export function parseScalarEdit(text, original) {
  const raw = String(text ?? '');
  const leaf = ejsonLeaf(original);
  if (leaf) {
    if (leaf.key === '$date' && Number.isNaN(Date.parse(raw))) { return { ok: false, error: 'Not a date' }; }
    return { ok: true, value: { [leaf.key]: raw } };
  }
  if (typeof original === 'number') {
    const n = Number(raw.trim());
    return raw.trim() !== '' && Number.isFinite(n) ? { ok: true, value: n } : { ok: false, error: 'Not a number' };
  }
  if (typeof original === 'boolean') {
    if (/^true$/i.test(raw.trim())) { return { ok: true, value: true }; }
    if (/^false$/i.test(raw.trim())) { return { ok: true, value: false }; }
    return { ok: false, error: 'true or false' };
  }
  if (original === null) {
    try { return { ok: true, value: JSON.parse(raw) }; } catch { return { ok: true, value: raw }; }
  }
  return { ok: true, value: raw };
}

// ---- Review

const keyText = (key = {}) => Object.entries(key).map(([k, v]) => `${k} ${v}`).join(', ');

/**
 * The sentence the Review dialog proposes for Activity, from the changes: "Changed price of id 1182, added a row,
 * removed id 1169 in products"; an import: "Imported 4,998 rows from products.csv into products".
 */
export function describeChanges(changes = [], { table, importedFrom = null, importedCount = 0 } = {}) {
  const updates = changes.filter((c) => c.op === 'update');
  const inserts = changes.filter((c) => c.op === 'insert');
  const deletes = changes.filter((c) => c.op === 'delete');
  const parts = [];
  if (updates.length === 1) {
    parts.push(`changed ${Object.keys(updates[0].values || {}).join(', ')} of ${keyText(updates[0].key)}`);
  } else if (updates.length > 1) {
    const cols = [...new Set(updates.flatMap((u) => Object.keys(u.values || {})))];
    parts.push(`changed ${cols.join(', ')} in ${updates.length.toLocaleString('en-US')} rows`);
  }
  const imported = importedFrom ? Math.min(importedCount, inserts.length) : 0;
  if (imported) { parts.push(`imported ${imported.toLocaleString('en-US')} row${imported === 1 ? '' : 's'} from ${importedFrom}`); }
  const added = inserts.length - imported;
  if (added === 1) { parts.push('added a row'); } else if (added > 1) { parts.push(`added ${added.toLocaleString('en-US')} rows`); }
  if (deletes.length === 1) { parts.push(`removed ${keyText(deletes[0].key)}`); } else if (deletes.length > 1) { parts.push(`removed ${deletes.length.toLocaleString('en-US')} rows`); }
  if (!parts.length) { return ''; }
  const text = `${parts.join(', ')} ${imported && parts.length === 1 ? 'into' : 'in'} ${table}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The Redis hint's targets of a table's changes: `[{ name, ids }]`, the ids being the single-column keys touched. */
export function hintTargets(changes = [], table) {
  const ids = [];
  changes.forEach((c) => {
    const values = Object.values(c.key || {});
    if (values.length === 1 && values[0] !== null && values[0] !== undefined) { ids.push(String(values[0])); }
  });
  return table ? [{ name: table, ids: [...new Set(ids)] }] : [];
}
